/**
 * Kebab-menu dropdown shared between Google `CampaignCard` and
 * `MetaCampaignCard`.
 *
 * Renders the three options on the existing Google card — Edit name,
 * Pause/Enable (mutually exclusive based on current status), Delete —
 * with consistent styling. Each option is a callback prop so the parent
 * card wires it to platform-specific store actions (Google) or to
 * "coming in next phase" toasts (Meta in A3).
 *
 * Accessibility: `role="menu"` + `role="menuitem"` on each option. The
 * triggering button is the caller's responsibility (it lives inside the
 * card layout) — this component is only the dropdown itself.
 */
import { cn } from '@/utils/cn';

interface CampaignKebabMenuProps {
  /**
   * Current campaign status (uppercased). Used to pick between
   * "Pause campaign" (when ACTIVE/ENABLED) and "Enable campaign" (when
   * PAUSED). Other statuses (ARCHIVED, DELETED, REMOVED) get neither
   * option — those campaigns aren't actionable through this menu.
   */
  status: string | null | undefined;
  onEditName: () => void;
  onPause: () => void;
  onEnable: () => void;
  onDelete: () => void;
  /**
   * Label for the terminal/destructive action. Defaults to
   * "Delete campaign" (Google). Meta cards pass "Archive campaign"
   * because Meta has no DELETE verb at the campaign level — archive
   * is the terminal state per §8.1.
   */
  deleteLabel?: string;
  className?: string;
}

const ITEM_BASE = 'w-full text-left px-4 py-2 text-sm flex items-center gap-2';

export function CampaignKebabMenu({
  status,
  onEditName,
  onPause,
  onEnable,
  onDelete,
  deleteLabel = 'Delete campaign',
  className,
}: CampaignKebabMenuProps) {
  const normalized = (status ?? '').toUpperCase();
  const isPaused = normalized === 'PAUSED';
  const isActive = normalized === 'ACTIVE' || normalized === 'ENABLED';

  return (
    <div
      className={cn(
        'absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50',
        className,
      )}
      role="menu"
    >
      <div className="py-1">
        <button
          onClick={onEditName}
          className={cn(ITEM_BASE, 'text-gray-700 hover:bg-gray-100')}
          role="menuitem"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          Edit name
        </button>

        {isPaused && (
          <button
            onClick={onEnable}
            className={cn(ITEM_BASE, 'text-green-600 hover:bg-green-50')}
            role="menuitem"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Enable campaign
          </button>
        )}

        {isActive && (
          <button
            onClick={onPause}
            className={cn(ITEM_BASE, 'text-gray-700 hover:bg-gray-100')}
            role="menuitem"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Pause campaign
          </button>
        )}

        <button
          onClick={onDelete}
          className={cn(ITEM_BASE, 'text-red-600 hover:bg-red-50')}
          role="menuitem"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          {deleteLabel}
        </button>
      </div>
    </div>
  );
}
